import { useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import { SYSTEM_ROLE_LABELS, SYSTEM_ROLE_OPTIONS } from "@/lib/constants";
import {
  useMenuPermissions,
  useReorderMenuPermissions,
  type MenuKey,
} from "@/hooks/use-menu-permissions";
import {
  ORDERABLE_TOP_LEVEL_KEYS,
  subKeysOf,
  MENU_LABELS,
} from "@/lib/menu-registry";
import { toast } from "sonner";

// VIB-1179: drag&drop UI for sort_order per rolle. Hovedmeny-keys og
// innstillinger-subs avledes nå fra det sentrale menyregisteret
// (src/lib/menu-registry.ts), så lista ikke kan drive fra de andre menyflatene.

interface SortableRowProps {
  id: MenuKey;
  label: string;
  index: number;
}

function SortableRow({ id, label, index }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-md border border-border bg-card text-sm",
        isDragging && "opacity-50 shadow-lg ring-2 ring-primary z-10",
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="text-muted-foreground hover:text-foreground shrink-0 cursor-grab active:cursor-grabbing"
        aria-label={`Dra ${label} for å endre rekkefølge`}
      >
        <SrIcon name="grip-vertical" className="w-3.5 h-3.5" />
      </button>
      <span className="text-xs tabular-nums text-muted-foreground w-5 text-right">
        {index + 1}
      </span>
      <span className="flex-1">{label}</span>
    </div>
  );
}

interface OrderListProps {
  title: string;
  keys: MenuKey[];
  orderMap: Map<MenuKey, number>;
  onSave: (ordered: MenuKey[]) => void;
  isSaving: boolean;
}

function OrderList({ title, keys, orderMap, onSave, isSaving }: OrderListProps) {
  // role-skift håndteres av parent som remount-er listen via `key`-prop,
  // så vi trenger ikke sync-logic her — initial state settes ved mount.
  const initialOrder = useMemo(
    () =>
      [...keys].sort(
        (a, b) => (orderMap.get(a) ?? 999) - (orderMap.get(b) ?? 999),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bevisst kun ved mount, role-skift trigger remount
    [],
  );
  const [order, setOrder] = useState<MenuKey[]>(initialOrder);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(active.id as MenuKey);
    const newIndex = order.indexOf(over.id as MenuKey);
    if (oldIndex === -1 || newIndex === -1) return;
    setOrder((items) => arrayMove(items, oldIndex, newIndex));
  };

  const isDirty = useMemo(
    () => order.some((key, idx) => initialOrder[idx] !== key),
    [order, initialOrder],
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h4>
        <Button
          variant="default"
          size="sm"
          disabled={!isDirty || isSaving}
          onClick={() => onSave(order)}
        >
          {isSaving ? "Lagrer..." : "Lagre"}
        </Button>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <div className="space-y-1.5">
            {order.map((key, idx) => (
              <SortableRow
                key={key}
                id={key}
                index={idx}
                label={MENU_LABELS[key]}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

export function MenyRekkefolgePanel() {
  const { data: permissions, isLoading } = useMenuPermissions();
  const reorder = useReorderMenuPermissions();
  const [selectedRole, setSelectedRole] = useState<string>("admin");

  if (isLoading) return null;

  // Bygg en lookup-map for valgt rolle: menu_key -> sort_order
  const orderMap = new Map<MenuKey, number>();
  (permissions ?? [])
    .filter((p) => p.role === selectedRole)
    .forEach((p) => orderMap.set(p.menu_key, p.sort_order));

  const handleSave = (ordered: MenuKey[]) => {
    reorder.mutate(
      {
        role: selectedRole,
        ordered: ordered.map((menu_key, idx) => ({
          menu_key,
          sort_order: idx + 1,
        })),
      },
      {
        onSuccess: () =>
          toast.success(
            `Rekkefølge oppdatert for ${SYSTEM_ROLE_LABELS[selectedRole]}`,
          ),
        onError: (err) => toast.error(`Feil: ${(err as Error).message}`),
      },
    );
  };

  return (
    <Card>
      <CardContent className="p-6 space-y-6">
        <div className="flex items-center gap-2">
          <SrIcon name="grip-vertical" className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-display text-base">Sidebar-rekkefølge per rolle</h2>
        </div>
        <p className="text-xs text-muted-foreground -mt-3">
          Dra menypunkter for å endre rekkefølgen de vises i sidebaren for valgt
          rolle. Endringer trer i kraft etter at queryen er invalidert.
        </p>

        <div className="space-y-2">
          <label className="text-xs font-medium" htmlFor="rolle-velger">
            Rolle
          </label>
          <Select value={selectedRole} onValueChange={setSelectedRole}>
            <SelectTrigger id="rolle-velger" className="w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SYSTEM_ROLE_OPTIONS.map((role) => (
                <SelectItem key={role} value={role}>
                  {SYSTEM_ROLE_LABELS[role]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <OrderList
          // key={selectedRole} sørger for remount når rolle endres,
          // slik at initialOrder hentes på nytt fra orderMap.
          key={`top-${selectedRole}`}
          title="Hovedmeny"
          keys={ORDERABLE_TOP_LEVEL_KEYS}
          orderMap={orderMap}
          onSave={handleSave}
          isSaving={reorder.isPending}
        />

        <OrderList
          key={`subs-${selectedRole}`}
          title="Innstillinger-undermeny"
          keys={subKeysOf("innstillinger")}
          orderMap={orderMap}
          onSave={handleSave}
          isSaving={reorder.isPending}
        />
      </CardContent>
    </Card>
  );
}
